import { RoundedLogo } from '@assets/newlogo';
import DesktopAuthWrapper from '@components/atoms/DesktopAuthWrapper';
import { yupResolver } from '@hookform/resolvers/yup';
import { useVerify } from '@hooks/api';
import { hasErrorCode, ErrorCode } from '@models/api/errors';
import { authErrorCode, login, reauth } from '@services/analytics';
import { useLoadingModal } from '@hooks/useLoadingModal';
import { useReauthParamsURL } from '@hooks/useReauthParams';
import { useRedirect } from '@hooks/useRedirect';
import { useTitle } from '@hooks/useTitle';
import {
    ButtonLabel,
    PasswordField,
    SimpleDivider,
} from '@moreharvest/component-library';
import {
    AnalyticsAuthErrorCode,
    AnalyticsAuthMethod,
    AnalyticsAuthStage,
    listOf,
    PermissionScope,
    SecretType,
    VerifyAuthRequest,
} from 'mh-client';
import { useAppSelector } from '@store/index';
import { selectEmail } from '@store/reducers/profile';
import { FormattedMessage } from '@utils/i18n';
import { ReactNode, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { object, string } from 'yup';

export interface LoginWithPasswordInputs extends FieldValues {
    password: string;
}

const LoginWithPassword = () => {
    useTitle('account_password_login', 'enterPassword');
    const navigate = useNavigate();
    const { mutateAsync: verify, isPending } = useVerify();
    const email = useAppSelector(selectEmail);
    const { url, anotherMethodUrl, isReauth } = useReauthParamsURL();
    const fireAuth = isReauth ? reauth : login;
    const [errorMessage, setErrorMessage] = useState<ReactNode>();
    useRedirect(email.length > 0, '/login');

    const schema = object().shape({
        password: string().required('Please enter a valid password'),
    });
    const { register, handleSubmit, watch } = useForm<LoginWithPasswordInputs>({
        defaultValues: {
            password: '',
        },
        resolver: yupResolver(schema),
    });

    const onSubmit = async (data: LoginWithPasswordInputs) => {
        const scopes = url.includes('security')
            ? listOf([PermissionScope.basic, PermissionScope.security])
            : listOf([PermissionScope.basic]);

        fireAuth({
            method: AnalyticsAuthMethod.Password,
            stage: AnalyticsAuthStage.Initiated,
        });

        try {
            const response = await verify(
                new VerifyAuthRequest(
                    email,
                    SecretType.password,
                    data.password,
                    scopes
                )
            );
            if (response.twoFactorRequired) {
                navigate('/login/totp' + anotherMethodUrl);
            } else {
                fireAuth({
                    method: AnalyticsAuthMethod.Password,
                    stage: AnalyticsAuthStage.Success,
                });
                navigate('/');
            }
        } catch (error: unknown) {
            fireAuth({
                method: AnalyticsAuthMethod.Password,
                stage: AnalyticsAuthStage.Failed,
                error_code: authErrorCode(
                    error,
                    AnalyticsAuthErrorCode.InvalidPassword
                ),
            });
            if (hasErrorCode(error, ErrorCode.InvalidPassword)) {
                setErrorMessage(
                    <FormattedMessage id="login_password_incorrect_password" />
                );
            } else {
                setErrorMessage(
                    <FormattedMessage id="oops_something_went_wrong" />
                );
            }
        }
    };

    useLoadingModal(isPending);

    const passwordValue = watch('password');

    return (
        <DesktopAuthWrapper>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col items-center gap-y-12 py-8 w-full"
            >
                <RoundedLogo size="small" />
                <h4 className="Label-Large w-full text-center break-all semibold">
                    {email}
                </h4>

                <div className="flex flex-col gap-y-8 w-full">
                    <PasswordField
                        variant="default"
                        placeholder="Password"
                        id="passwordLogin_input_password"
                        value={passwordValue}
                        register={register('password')}
                        errorMessage={errorMessage}
                    />
                    <ButtonLabel
                        size="medium"
                        variant="solid"
                        type="submit"
                        id="passwordLogin_CTA_continueWithPassword"
                    >
                        <FormattedMessage id="continue_upper" />
                    </ButtonLabel>
                    <SimpleDivider>
                        <FormattedMessage id="login_password_or" />
                    </SimpleDivider>
                    <ButtonLabel
                        size="medium"
                        variant="secondary"
                        type="button"
                        onClick={() => {
                            fireAuth({
                                method: AnalyticsAuthMethod.MagicLink,
                                stage: AnalyticsAuthStage.MethodSwitch,
                            });
                            navigate('/login/send-magic-link', {
                                state: {
                                    force: true,
                                },
                            });
                        }}
                        id="passwordLogin_CTA_loginWithEmail"
                    >
                        <FormattedMessage id="use_another_method" />
                    </ButtonLabel>
                </div>
            </form>
        </DesktopAuthWrapper>
    );
};

export default LoginWithPassword;
